import type {Mapper} from 'framework';
import type {
  ScheduleCalendar,
  ScheduleCalendarDay,
  ScheduleCalendarItem,
} from '../../domain/schedule-calendar';
import type {ScheduleOccurrence} from '../../domain/schedule-occurrence';
import type {ScheduleCalendarDto, ScheduleCalendarItemDto} from '../dto/schedule.dto';

/**
 * Narrows the item's ids into the occurrence the skip/move/swap/start commands send.
 * A session the schedule never planned has no occurrence and therefore no actions.
 */
const toOccurrence = (item: ScheduleCalendarItemDto): ScheduleOccurrence | null => {
  if (item.origin === 'pattern') {
    return item.slotId === null
      ? null
      : {source: 'pattern', slotId: item.slotId, occurrenceDate: item.occurrenceDate};
  }

  if (item.origin === 'one_off') {
    return item.oneOffId === null ? null : {source: 'one_off', oneOffId: item.oneOffId};
  }

  return null;
};

const toItem = (item: ScheduleCalendarItemDto): ScheduleCalendarItem => ({
  kind: item.kind,
  origin: item.origin,
  state: item.state,
  exception: item.exception,
  templateId: item.templateId,
  templateName: item.templateName,
  workoutId: item.workoutId,
  workoutName: item.workoutName,
  occurrenceDate: item.occurrenceDate,
  displayDate: item.displayDate,
  occurrence: toOccurrence(item),
  canStart: item.canStart,
});

const toDay = (day: ScheduleCalendarDto['days'][number]): ScheduleCalendarDay => ({
  date: day.date,
  items: day.items.map(toItem),
});

/** Translates the RPC calendar into the schedule slice's read model. */
export class ScheduleCalendarDtoToScheduleCalendarMapper implements Mapper<
  ScheduleCalendarDto,
  ScheduleCalendar
> {
  transform(dto: ScheduleCalendarDto): ScheduleCalendar {
    return {
      from: dto.from,
      to: dto.to,
      today: dto.today,
      days: dto.days.map(toDay),
    };
  }
}
